import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { DataTable, type Column } from "@/components/ui/data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { usePersistedSort } from "@/lib/use-persisted-sort";
import { AlertTriangle, Plus, Search, MapPin } from "lucide-react";

interface CustomerRow {
    id: string;
    name: string;
    email: string;
    phone: string;
    company: string;
    location_count: number;
    open_job_count: number;
    service_hold: boolean;
    created_at: string;
}

const PAGE_SIZE = 25;

/** Must match CUSTOMER_LIST_SORT on the server (crm.ts). */
const CUSTOMER_SORT_KEYS = ["name", "email", "phone", "open_job_count", "location_count"] as const;

export default function CustomersPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const page = Number(searchParams.get("page")) || 1;
    const search = searchParams.get("q") ?? "";
    const [searchInput, setSearchInput] = useState(search);
    // Sorted on the server — the list is paged, so a browser sort would only
    // reorder the 25 rows on screen. Remembered per user until they re-sort.
    const sort = usePersistedSort({
        tableId: "customers.list",
        defaultKey: "name",
        validKeys: CUSTOMER_SORT_KEYS,
    });

    const { data, isLoading } = useQuery({
        queryKey: ["customers", page, search, sort.sortKey, sort.sortDir],
        // Wait for the saved sort so the list doesn't load twice.
        enabled: sort.loaded,
        queryFn: async () => {
            const params = new URLSearchParams({
                limit: String(PAGE_SIZE),
                offset: String((page - 1) * PAGE_SIZE),
            });
            if (search) params.set("q", search);
            if (sort.sortKey) params.set("sort", sort.sortKey);
            params.set("dir", sort.sortDir);
            const data = await api.get<{
                customers: Array<{
                    id: string;
                    company_name: string;
                    customer_type: string | null;
                    is_active: boolean;
                    created_at: string;
                    email?: string;
                    phone?: string;
                    location_count?: number;
                    open_job_count?: number;
                    service_hold?: boolean;
                }>;
                total: number;
            }>(`/api/customers?${params}`);
            const rows: CustomerRow[] = data.customers.map((c) => ({
                id: c.id,
                name: c.company_name,
                email: c.email ?? "",
                phone: c.phone ?? "",
                company: c.company_name,
                location_count: c.location_count ?? 0,
                open_job_count: c.open_job_count ?? 0,
                service_hold: c.service_hold ?? false,
                created_at: c.created_at,
            }));
            return { rows, total: data.total };
        },
    });

    const totalPages = Math.ceil((data?.total ?? 0) / PAGE_SIZE);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setSearchParams((p) => {
            p.set("q", searchInput);
            p.set("page", "1");
            return p;
        });
    };

    const columns: Column<CustomerRow>[] = [
        {
            key: "name",
            header: "Name",
            sortable: true,
            render: (r) => (
                <span className="inline-flex flex-wrap items-center gap-2">
                    <span className="font-medium">{r.name}</span>
                    {/* The detail page's red flag, on the list too, so nobody
                        books a held account without opening it first. */}
                    {r.service_hold && (
                        <Badge variant="destructive" className="gap-1">
                            <AlertTriangle className="h-3 w-3" /> Do Not Service
                        </Badge>
                    )}
                </span>
            ),
        },
        {
            key: "email",
            header: "Email",
            sortable: true,
            render: (r) => r.email || <span className="text-foreground/80">—</span>,
        },
        {
            key: "phone",
            header: "Phone",
            sortable: true,
            render: (r) => r.phone || <span className="text-foreground/80">—</span>,
        },
        {
            key: "open_job_count",
            header: "Open jobs",
            sortable: true,
            firstSortDir: "desc",
            className: "w-32 whitespace-nowrap text-center",
            render: (r) =>
                r.open_job_count > 0 ? (
                    <span className="inline-flex min-w-7 justify-center rounded-md bg-primary px-1.5 py-0.5 text-xs font-bold tabular-nums text-primary-foreground">
                        {r.open_job_count}
                    </span>
                ) : (
                    <span className="text-foreground/50">—</span>
                ),
        },
        {
            key: "location_count",
            header: "Locations",
            sortable: true,
            firstSortDir: "desc",
            className: "w-28 text-center",
            render: (r) => (
                <span className="inline-flex items-center gap-1 text-sm">
                    <MapPin className="h-3 w-3 text-foreground/80" />
                    {r.location_count}
                </span>
            ),
        },
    ];

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-black tracking-tight text-shell-foreground">Customers</h1>
                    <p className="text-shell-foreground/80 text-sm mt-1">
                        Manage customers, contacts, and service locations.
                    </p>
                </div>
                <Button onClick={() => navigate("/customers/new")}>
                    <Plus className="h-4 w-4" />
                    New Customer
                </Button>
            </div>

            <form onSubmit={handleSearch} className="flex gap-2 max-w-sm">
                <Input
                    placeholder="Search customers..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                />
                <Button type="submit" variant="outline" size="icon">
                    <Search className="h-4 w-4" />
                </Button>
            </form>

            <DataTable
                columns={columns}
                data={data?.rows ?? []}
                keyField="id"
                loading={isLoading || !sort.loaded}
                onRowClick={(row) => navigate(`/customers/${row.id}`)}
                emptyMessage="No customers found — try a different search"
                controlledSort={{
                    key: sort.sortKey,
                    dir: sort.sortDir,
                    onChange: (key, firstDir) => {
                        sort.toggleSort(key, firstDir);
                        // Page 3 of a different order is nobody's intent.
                        setSearchParams((p) => {
                            p.set("page", "1");
                            return p;
                        });
                    },
                }}
                page={page}
                totalPages={totalPages}
                onPageChange={(p) =>
                    setSearchParams((params) => {
                        params.set("page", String(p));
                        return params;
                    })
                }
            />
        </div>
    );
}
